import { supabase } from '../lib/supabase'
import {
  Module,
  ModuleWithPermissions,
  UserGroup,
  Permission,
  PermissionWithDetails,
  UserProfile,
  UserProfileWithDetails,
  CreateModuleData,
  UpdateModuleData,
  CreateUserGroupData,
  UpdateUserGroupData,
  CreatePermissionData,
  UpdatePermissionData,
  ApiResponse,
  PaginationParams,
  PaginatedResponse
} from '../types'

// =====================================================
// SERVIÇOS DE MÓDULOS
// =====================================================

export const modulesService = {
  // Obter todos os módulos
  async getAll(): Promise<ApiResponse<Module[]>> {
    try {
      const { data, error } = await supabase
        .from('modules')
        .select('*')
        .order('order_index', { ascending: true })

      if (error) throw error

      return { data: data || [], success: true }
    } catch (error) {
      return { data: [], error: (error as Error).message, success: false }
    }
  },

  // Obter módulos do usuário atual
  async getUserModules(): Promise<ApiResponse<ModuleWithPermissions[]>> {
    try {
      const { data, error } = await supabase
        .rpc('get_user_modules')

      if (error) throw error

      return { data: data || [], success: true }
    } catch (error) {
      return { data: [], error: (error as Error).message, success: false }
    }
  },

  // Obter módulos ativos com permissões para um grupo específico
  async getActiveWithPermissionsByGroup(userGroupId: string): Promise<ApiResponse<ModuleWithPermissions[]>> {
    try {
      // Buscar módulos ativos ordenados por order_index
      const { data: modulesData, error: modulesError } = await supabase
        .from('modules')
        .select('*')
        .eq('is_active', true)
        .order('order_index', { ascending: true })

      if (modulesError) throw modulesError

      // Buscar permissões do grupo
      const { data: permsData, error: permsError } = await supabase
        .from('permissions')
        .select('module_id, can_show, can_insert, can_update, can_delete')
        .eq('user_group_id', userGroupId)

      if (permsError) throw permsError

      const permsByModuleId = new Map<string, { can_show: boolean; can_insert: boolean; can_update: boolean; can_delete: boolean }>(
        (permsData || []).map((p: any) => [
          p.module_id,
          {
            can_show: !!p.can_show,
            can_insert: !!p.can_insert,
            can_update: !!p.can_update,
            can_delete: !!p.can_delete
          }
        ])
      )

      const result: ModuleWithPermissions[] = (modulesData || []).map((m: any) => {
        const p = permsByModuleId.get(m.id)
        return {
          ...m,
          can_show: p ? p.can_show : false,
          can_insert: p ? p.can_insert : false,
          can_update: p ? p.can_update : false,
          can_delete: p ? p.can_delete : false
        } as ModuleWithPermissions
      })

      return { data: result, success: true }
    } catch (error) {
      return { data: [], error: (error as Error).message, success: false }
    }
  },

  // Obter módulo por ID
  async getById(id: string): Promise<ApiResponse<Module>> {
    try {
      const { data, error } = await supabase
        .from('modules')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error

      return { data, success: true }
    } catch (error) {
      return { data: {} as Module, error: (error as Error).message, success: false }
    }
  },

  // Obter módulo por nome
  async getByName(name: string): Promise<ApiResponse<Module>> {
    try {
      const { data, error } = await supabase
        .from('modules')
        .select('*')
        .eq('name', name)
        .single()

      if (error) throw error

      return { data, success: true }
    } catch (error) {
      return { data: {} as Module, error: (error as Error).message, success: false }
    }
  },

  // Criar módulo
  async create(moduleData: CreateModuleData): Promise<ApiResponse<Module>> {
    try {
      const { data, error } = await supabase
        .from('modules')
        .insert(moduleData)
        .select()
        .single()

      if (error) throw error

      return { data, success: true }
    } catch (error) {
      return { data: {} as Module, error: (error as Error).message, success: false }
    }
  },

  // Atualizar módulo
  async update(id: string, moduleData: UpdateModuleData): Promise<ApiResponse<Module>> {
    try {
      const { data, error } = await supabase
        .from('modules')
        .update(moduleData)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error

      return { data, success: true }
    } catch (error) {
      return { data: {} as Module, error: (error as Error).message, success: false }
    }
  },

  // Deletar módulo
  async delete(id: string): Promise<ApiResponse<void>> {
    try {
      const { error } = await supabase
        .from('modules')
        .delete()
        .eq('id', id)

      if (error) throw error

      return { data: undefined, success: true }
    } catch (error) {
      return { data: undefined, error: (error as Error).message, success: false }
    }
  }
}

// =====================================================
// SERVIÇOS DE GRUPOS DE USUÁRIOS
// =====================================================

export const userGroupsService = {
  // Obter todos os grupos
  async getAll(): Promise<ApiResponse<UserGroup[]>> {
    try {
      const { data, error } = await supabase
        .from('user_groups')
        .select('*')
        .order('display_name', { ascending: true })

      if (error) throw error

      return { data: data || [], success: true }
    } catch (error) {
      return { data: [], error: (error as Error).message, success: false }
    }
  },

  // Obter grupo por ID
  async getById(id: string): Promise<ApiResponse<UserGroup>> {
    try {
      const { data, error } = await supabase
        .from('user_groups')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error

      return { data, success: true }
    } catch (error) {
      return { data: {} as UserGroup, error: (error as Error).message, success: false }
    }
  },

  // Criar grupo
  async create(groupData: CreateUserGroupData): Promise<ApiResponse<UserGroup>> {
    try {
      const { data, error } = await supabase
        .from('user_groups')
        .insert(groupData)
        .select()
        .single()

      if (error) throw error

      return { data, success: true }
    } catch (error) {
      return { data: {} as UserGroup, error: (error as Error).message, success: false }
    }
  },

  // Atualizar grupo
  async update(id: string, groupData: UpdateUserGroupData): Promise<ApiResponse<UserGroup>> {
    try {
      const { data, error } = await supabase
        .from('user_groups')
        .update(groupData)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error

      return { data, success: true }
    } catch (error) {
      return { data: {} as UserGroup, error: (error as Error).message, success: false }
    }
  },

  // Deletar grupo
  async delete(id: string): Promise<ApiResponse<void>> {
    try {
      const { error } = await supabase
        .from('user_groups')
        .delete()
        .eq('id', id)

      if (error) throw error

      return { data: undefined, success: true }
    } catch (error) {
      return { data: undefined, error: (error as Error).message, success: false }
    }
  }
}

// =====================================================
// SERVIÇOS DE PERMISSÕES
// =====================================================

export const permissionsService = {
  // Obter todas as permissões
  async getAll(): Promise<ApiResponse<PermissionWithDetails[]>> {
    try {
      const { data, error } = await supabase
        .from('permissions')
        .select(`
          *,
          module:modules(*),
          user_group:user_groups(*)
        `)
        .order('created_at', { ascending: false })

      if (error) throw error

      return { data: data || [], success: true }
    } catch (error) {
      return { data: [], error: (error as Error).message, success: false }
    }
  },

  // Obter permissões por grupo
  async getByGroup(groupId: string): Promise<ApiResponse<PermissionWithDetails[]>> {
    try {
      const { data, error } = await supabase
        .from('permissions')
        .select(`
          *,
          module:modules(*),
          user_group:user_groups(*)
        `)
        .eq('user_group_id', groupId)

      if (error) throw error

      return { data: data || [], success: true }
    } catch (error) {
      return { data: [], error: (error as Error).message, success: false }
    }
  },

  // Verificar permissão do usuário
  async checkPermission(moduleName: string, permissionType: string): Promise<ApiResponse<boolean>> {
    try {
      const { data, error } = await supabase
        .rpc('check_user_permission', {
          module_name: moduleName,
          permission_type: permissionType
        })

      if (error) throw error

      return { data: data || false, success: true }
    } catch (error) {
      return { data: false, error: (error as Error).message, success: false }
    }
  },

  // Criar permissão
  async create(permissionData: CreatePermissionData): Promise<ApiResponse<Permission>> {
    try {
      const { data, error } = await supabase
        .from('permissions')
        .insert(permissionData)
        .select()
        .single()

      if (error) throw error

      return { data, success: true }
    } catch (error) {
      return { data: {} as Permission, error: (error as Error).message, success: false }
    }
  },

  // Atualizar permissão
  async update(id: string, permissionData: UpdatePermissionData): Promise<ApiResponse<Permission>> {
    try {
      const { data, error } = await supabase
        .from('permissions')
        .update(permissionData)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error

      return { data, success: true }
    } catch (error) {
      return { data: {} as Permission, error: (error as Error).message, success: false }
    }
  },

  // Deletar permissão
  async delete(id: string): Promise<ApiResponse<void>> {
    try {
      const { error } = await supabase
        .from('permissions')
        .delete()
        .eq('id', id)

      if (error) throw error

      return { data: undefined, success: true }
    } catch (error) {
      return { data: undefined, error: (error as Error).message, success: false }
    }
  }
}

// =====================================================
// SERVIÇOS DE USUÁRIOS
// =====================================================

export const usersService = {
  // Obter todos os usuários
  async getAll(): Promise<ApiResponse<UserProfileWithDetails[]>> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          *,
          user_group:user_groups(*),
          activated_by_user:profiles!activated_by(id, name)
        `)
        .order('created_at', { ascending: false })

      if (error) throw error

      return { data: data || [], success: true }
    } catch (error) {
      return { data: [], error: (error as Error).message, success: false }
    }
  },

  // Obter usuário por ID
  async getById(id: string): Promise<ApiResponse<UserProfileWithDetails>> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          *,
          user_group:user_groups(*),
          activated_by_user:profiles!activated_by(id, name)
        `)
        .eq('id', id)
        .single()

      if (error) throw error

      return { data, success: true }
    } catch (error) {
      return { data: {} as UserProfileWithDetails, error: (error as Error).message, success: false }
    }
  },

  // Ativar usuário
  async activateUser(userId: string): Promise<ApiResponse<boolean>> {
    try {
      const { data, error } = await supabase
        .rpc('activate_user', {
          user_id: userId
        })

      if (error) throw error

      return { data: data || false, success: true }
    } catch (error) {
      return { data: false, error: (error as Error).message, success: false }
    }
  },

  // Desativar usuário
  async deactivateUser(userId: string): Promise<ApiResponse<UserProfile>> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update({
          is_active: false,
          activated_by: null,
          activated_at: null
        })
        .eq('id', userId)
        .select()
        .single()

      if (error) throw error

      return { data, success: true }
    } catch (error) {
      return { data: {} as UserProfile, error: (error as Error).message, success: false }
    }
  },

  // Atualizar grupo do usuário
  async updateUserGroup(userId: string, userGroupId: string): Promise<ApiResponse<UserProfile>> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update({ user_group_id: userGroupId })
        .eq('id', userId)
        .select()
        .single()

      if (error) throw error

      return { data, success: true }
    } catch (error) {
      return { data: {} as UserProfile, error: (error as Error).message, success: false }
    }
  },

  // Atualizar perfil do usuário
  async updateProfile(userId: string, profileData: Partial<UserProfile>): Promise<ApiResponse<UserProfile>> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update(profileData)
        .eq('id', userId)
        .select()
        .single()

      if (error) throw error

      return { data, success: true }
    } catch (error) {
      return { data: {} as UserProfile, error: (error as Error).message, success: false }
    }
  }
}

// =====================================================
// SERVIÇOS DE ESTATÍSTICAS
// =====================================================

export const statsService = {
  // Obter estatísticas de módulos
  async getModuleStats(): Promise<ApiResponse<any>> {
    try {
      const { data, error } = await supabase
        .from('modules')
        .select('is_active')

      if (error) throw error

      const stats = {
        total_modules: data?.length || 0,
        active_modules: data?.filter(m => m.is_active).length || 0,
        inactive_modules: data?.filter(m => !m.is_active).length || 0
      }

      return { data: stats, success: true }
    } catch (error) {
      return { data: {}, error: (error as Error).message, success: false }
    }
  },

  // Obter estatísticas de usuários
  async getUserStats(): Promise<ApiResponse<any>> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('is_active')

      if (error) throw error

      const stats = {
        total_users: data?.length || 0,
        active_users: data?.filter(u => u.is_active).length || 0,
        inactive_users: data?.filter(u => !u.is_active).length || 0,
        pending_activation: data?.filter(u => !u.is_active).length || 0
      }

      return { data: stats, success: true }
    } catch (error) {
      return { data: {}, error: (error as Error).message, success: false }
    }
  }
}
